import { SelectProjectRole } from "@components/role/select-project-role";
import { RoleChangeSuccessDescription } from "@components/table/members/members-column/role-column/role-change-success-description";
import { isSuccessResponse } from "@context-providers/error-boundary/error-boundary-utils";
import { BaseCompanyProjectIdProps } from "@custom-types/base-types";
import { MemberIdentityRoleProps } from "@custom-types/member-types";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { useToast } from "@hooks/use-toast";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { isSelectedProjectEditableSelector } from "@store/projects/projects-selector";
import { updateMemberRoleInProjectSingle } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { DEFAULT_TEXT_FONT_SIZE } from "@styles/common-styles";
import { hasUserValidCompanyRole } from "@utils/access-control/company/company-access-control";
import { formatUserRoleType } from "@utils/data-display";
import { useMemo } from "react";

type Props = BaseCompanyProjectIdProps & MemberIdentityRoleProps;

/** Render and adjust member roles in project context. */
export function ProjectMemberRole({
  companyId,
  projectId,
  memberIdentityRole,
}: Props): JSX.Element {
  const { showToast } = useToast();
  const dispatch = useAppDispatch();
  const isProjectEditable = useAppSelector(isSelectedProjectEditableSelector());
  const currentUser = useAppSelector(currentUserSelector);
  const { canChangeRole } = useHasUserValidPermissionProjectLevel();

  /** Determine if the change role dropdown should be hidden */
  const shouldHideDropdown = useMemo(() => {
    const isCompanyAdmin = hasUserValidCompanyRole({
      currentUser,
      requiredCompanyRoles: ["COMPANY_EXECUTIVE"],
    });

    const isCurrentUserProjectAdmin =
      memberIdentityRole.identity === currentUser?.identity &&
      memberIdentityRole.role === "RESOURCE_ADMIN";

    const isResourceOwner = memberIdentityRole.role === "RESOURCE_OWNER";

    /**
     * Should hide role change dropdown if:
     * - Project is not editable
     * - User doesn't have a valid role to change
     * - Member is resource owner (Project manager)
     * - Current user is a project admin but not a company admin of this project
     */
    const shouldHideDropdown =
      !isProjectEditable ||
      !canChangeRole ||
      isResourceOwner ||
      (isCurrentUserProjectAdmin && !isCompanyAdmin);
    return shouldHideDropdown;
  }, [canChangeRole, currentUser, isProjectEditable, memberIdentityRole]);

  // Return non-editable member role
  if (shouldHideDropdown) {
    return (
      <span style={{ fontSize: DEFAULT_TEXT_FONT_SIZE }}>
        {formatUserRoleType(memberIdentityRole.role)}
      </span>
    );
  }

  /** Trigger updating member role and show success message */
  async function onRoleChange(
    role: FaroCoreApiTypes.ProjectRole
  ): Promise<void> {
    const result = await dispatch(
      updateMemberRoleInProjectSingle({
        companyId,
        projectId,
        userIdentity: memberIdentityRole.identity,
        payload: { role },
      })
    );

    // Errors are already handled by the slice, only notify about a successful result
    if (isSuccessResponse(result)) {
      showToast({
        message: "Role Changed",
        description: (
          <RoleChangeSuccessDescription role={formatUserRoleType(role)} />
        ),
        type: "success",
      });
    }
  }

  return (
    <SelectProjectRole
      selectedRole={
        memberIdentityRole.role as FaroCoreApiTypes.AssignmentRoleBySubjectType["project"]
      }
      onChange={onRoleChange}
      isTableCell={true}
    />
  );
}
